import { Copy, MessageCircle, Radio, Send, Smartphone, UserRound } from "lucide-react";
import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import {
  getPushChannels,
  postBarkDisable,
  postBarkSave,
  postTelegramDisable,
  postTelegramLink,
  postWebPushSendNotification,
} from "@client/api/endpoints";
import { useAsync } from "@client/hooks/useAsync";
import { t } from "@client/lib/dictionary";
import { Badge } from "@client/components/ui/badge";
import { Button } from "@client/components/ui/button";

const tc = t.settings.push.channels;

const copy = async (text: string) => {
  try {
    await navigator.clipboard.writeText(text);
    toast.success(tc.copied);
  } catch {
    toast.error("Kopírování selhalo");
  }
};

/**
 * Free push kanály pro iOS/Brave — všechny se vážou na přihlášeného člověka,
 * proto stojí pod jednou hlavičkou a odděleně od seznamu web-push zařízení.
 *
 * ntfy se tu **nezapíná**: topic je `demirel-<jméno>` odvozený ze jména (channels.ts),
 * takže se jen vypíše k opsání do appky. Dřív tu bylo tlačítko „Zapnout ntfy", které
 * generovalo náhodný topic do DB — jeden stav navíc, který šel rozejít s ACL na serveru.
 */
const PushChannels: React.FC = () => {
  const { data, refetch } = useAsync(() => getPushChannels(), []);
  const status = data?.responseObject;

  // Po návratu z Telegramu (focus okna) přenačíst stav (spárování běží server-side).
  useEffect(() => {
    const onFocus = () => void refetch();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [refetch]);

  const connectTelegram = useCallback(async () => {
    const res = await postTelegramLink();
    if (res.success && res.responseObject?.deepLink) {
      window.open(res.responseObject.deepLink, "_blank", "noopener");
    }
  }, []);

  const disconnectTelegram = useCallback(async () => {
    await postTelegramDisable();
    await refetch();
  }, [refetch]);

  const [barkKey, setBarkKey] = useState("");
  const saveBark = useCallback(async () => {
    if (!barkKey.trim()) return;
    const res = await postBarkSave(barkKey.trim());
    if (res.success) {
      setBarkKey("");
      toast.success("Bark uložen.");
      await refetch();
    } else toast.error(res.message);
  }, [barkKey, refetch]);

  const disableBark = useCallback(async () => {
    await postBarkDisable();
    await refetch();
  }, [refetch]);

  const sendTest = useCallback(async () => {
    const res = await postWebPushSendNotification();
    if (res.success) toast.success("Test odeslán do kanálů.");
    else toast.error(res.message);
  }, []);

  if (!status) return null;
  // Když není na serveru nakonfigurovaný ani jeden kanál, sekci skryjeme.
  if (!status.ntfy.enabled && !status.telegram.enabled && !status.bark.enabled) return null;

  return (
    <div className="border-t border-border pt-4">
      <h4 className="text-sm font-semibold">{tc.title}</h4>
      <p className="text-xs text-muted-foreground mt-1">{tc.description}</p>

      <div className="mt-4 flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
        <UserRound className="h-3.5 w-3.5" />
        {tc.groupTitle}
      </div>
      <p className="mt-1 text-xs text-muted-foreground">{tc.groupHint}</p>

      {/* ntfy — topic odvozený ze jména, nic se nezapíná */}
      {status.ntfy.enabled && (
        <div className="mt-2 rounded-md border border-border p-3">
          <div className="flex items-center gap-1.5 text-sm font-medium">
            <Radio className="h-4 w-4" />
            {tc.ntfyTitle}
            <Badge variant="secondary" className="ml-1">
              {tc.ntfyOwn}
            </Badge>
          </div>
          <div className="mt-2 flex flex-col gap-1.5 text-xs">
            <Field label={tc.ntfyServer} value={status.ntfy.serverUrl} />
            <Field label={tc.ntfyTopic} value={status.ntfy.topic} />
            <p className="text-muted-foreground">{tc.ntfyOwnHint}</p>
            <p className="text-muted-foreground">{tc.ntfyHelp}</p>
            <p className="text-muted-foreground">{tc.ntfyAccessHint}</p>
          </div>
        </div>
      )}

      {/* Telegram */}
      {status.telegram.enabled && (
        <div className="mt-2 rounded-md border border-border p-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1.5 text-sm font-medium">
              <MessageCircle className="h-4 w-4" />
              {tc.telegramTitle}
              {status.telegram.connected && (
                <Badge variant="default" className="ml-1">
                  {tc.telegramConnected}
                </Badge>
              )}
            </div>
            {status.telegram.connected ? (
              <Button variant="outline" size="sm" onClick={disconnectTelegram}>
                {tc.telegramDisable}
              </Button>
            ) : (
              <Button variant="default" size="sm" onClick={connectTelegram}>
                {tc.telegramConnect}
              </Button>
            )}
          </div>
          {!status.telegram.connected && <p className="mt-2 text-xs text-muted-foreground">{tc.telegramHelp}</p>}
        </div>
      )}

      {/* Bark (iOS) */}
      {status.bark.enabled && (
        <div className="mt-2 rounded-md border border-border p-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1.5 text-sm font-medium">
              <Smartphone className="h-4 w-4" />
              {tc.barkTitle}
              {status.bark.connected && (
                <Badge variant="default" className="ml-1">
                  {tc.barkConnected}
                </Badge>
              )}
            </div>
            {status.bark.connected && (
              <Button variant="outline" size="sm" onClick={disableBark}>
                {tc.barkDisable}
              </Button>
            )}
          </div>
          {!status.bark.connected && (
            <div className="mt-2 flex flex-col gap-1.5 text-xs">
              <Field label={tc.barkServer} value={status.bark.serverUrl} />
              <div className="flex items-center gap-2">
                <input
                  value={barkKey}
                  onChange={(e) => setBarkKey(e.target.value)}
                  placeholder={tc.barkPlaceholder}
                  className="flex-1 rounded-md border border-border bg-background px-2 py-1"
                />
                <Button variant="default" size="sm" onClick={saveBark} disabled={!barkKey.trim()}>
                  {tc.barkSave}
                </Button>
              </div>
              <p className="text-muted-foreground">{tc.barkHelp}</p>
            </div>
          )}
        </div>
      )}

      {(status.ntfy.enabled || status.telegram.connected || status.bark.connected) && (
        <Button variant="secondary" size="sm" className="mt-3" onClick={sendTest}>
          <Send className="h-4 w-4" />
          {tc.test}
        </Button>
      )}
    </div>
  );
};

const Field: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="flex items-center gap-2">
    <span className="text-muted-foreground w-14 shrink-0">{label}:</span>
    <code className="truncate rounded bg-muted px-1.5 py-0.5">{value}</code>
    <Button variant="ghost" size="icon" className="h-6 w-6 shrink-0" onClick={() => copy(value)} aria-label={tc.copy}>
      <Copy className="h-3.5 w-3.5" />
    </Button>
  </div>
);

export default React.memo(PushChannels);
